import React, { useEffect, useState } from 'react';
import Icon from '../../common/Icon/Icon';
import Loading from '../../common/Loading/Loading';
import type { StudentBodyPhotoAssessment } from '../../../services/PhotoService';
import { formatDate } from '../../../utils';
import PhotoGallery from '../PhotoGallery/PhotoGallery';
import styles from './BodyPhotoHistoryPanel.module.css';

interface BodyPhotoHistoryPanelProps {
  assessments: StudentBodyPhotoAssessment[];
  isLoading?: boolean;
}

const formatAssessmentDateLabel = (assessmentDate: string): string => {
  if (!assessmentDate || assessmentDate === 'legacy') {
    return 'Envio sem data (legado)';
  }

  return formatDate(assessmentDate);
};

const BodyPhotoHistoryPanel: React.FC<BodyPhotoHistoryPanelProps> = ({
  assessments,
  isLoading = false,
}) => {
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (assessments.length === 0) {
      setExpandedDates(new Set());
      return;
    }

    setExpandedDates((current) => {
      const validDates = new Set(assessments.map((item) => item.assessmentDate));
      const next = new Set(Array.from(current).filter((date) => validDates.has(date)));
      if (next.size > 0) {
        return next;
      }

      return new Set([assessments[0].assessmentDate]);
    });
  }, [assessments]);

  const toggleDate = (assessmentDate: string) => {
    setExpandedDates((current) => {
      const next = new Set(current);
      if (next.has(assessmentDate)) {
        next.delete(assessmentDate);
      } else {
        next.add(assessmentDate);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className={styles.panel}>
        <Loading />
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <div className={styles.panel}>
        <p className={styles.empty}>Nenhuma foto enviada ainda.</p>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.list}>
        {assessments.map((assessment, index) => {
          const isExpanded = expandedDates.has(assessment.assessmentDate);
          const photoCount = assessment.photos.length;

          return (
            <div key={assessment.assessmentDate} className={styles.item}>
              <button
                type="button"
                className={styles.header}
                onClick={() => toggleDate(assessment.assessmentDate)}
                aria-expanded={isExpanded}
              >
                <span className={styles.headerMain}>
                  <Icon
                    name="expand_more"
                    size={20}
                    className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}
                  />
                  <span className={styles.dateLabel}>
                    {formatAssessmentDateLabel(assessment.assessmentDate)}
                  </span>
                  {index === 0 && (
                    <span className={styles.latestBadge}>Mais recente</span>
                  )}
                </span>
                <span className={styles.photoCount}>
                  {photoCount} {photoCount === 1 ? 'foto' : 'fotos'}
                </span>
              </button>

              {isExpanded && (
                <div className={styles.content}>
                  <PhotoGallery photos={assessment.photos} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BodyPhotoHistoryPanel;
